import * as React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import { Box, Button, Divider, Grid, Stack } from '@mui/material';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

import { CONFIG } from '~/config';

import NumberInput from '~/components/Form/NumberInput';

import { TripTravellers } from '~/types/common';

interface Props {
  passengers: TripTravellers;
  onAdultPassangersChange: (
    event:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => void;
  onChildrenPassangersChange: (
    event:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => void;
  onInfantPassangersChange: (
    event:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => void;
}

function TravelersNumberSelector({
  onAdultPassangersChange,
  onChildrenPassangersChange,
  onInfantPassangersChange,
  passengers,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDone = () => {
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'passenger-popover' : undefined;
  const totalPassengers = passengers.adults + passengers.children + passengers.infants;

  const maxAdultTravellers = CONFIG.app.maxAdultTravellers - passengers.children;
  const maxChildrenTravellers = CONFIG.app.maxChildrenTravellers - passengers.adults;
  const maxInfantTravellers = passengers.adults;

  const passengersCaption = totalPassengers < 2 ? 'Passenger' : 'Passengers';

  return (
    <Box>
      <Typography
        onClick={handleClick}
        sx={{
          fontSize: '1rem',
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <PeopleIcon sx={{ marginRight: '0.5rem' }} /> {totalPassengers} {passengersCaption}
        <ExpandMoreIcon />
      </Typography>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        id={id}
        onClose={handleClose}
        open={open}
      >
        <Box
          sx={{
            paddingTop: 1,
            paddingLeft: 2,
            paddingRight: 2,
            paddingBottom: 2,
            maxWidth: 280,
          }}
        >
          <Grid alignItems="center" container justifyContent="space-between" spacing={1}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  paddingBottom: 1,
                }}
                variant="h6"
              >
                Passengers
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">Adults (Over 11)</Typography>
            </Grid>
            <Grid>
              <NumberInput
                max={maxAdultTravellers}
                min={1}
                onChange={onAdultPassangersChange}
                value={passengers.adults}
              />
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">Children (2 - 11)</Typography>
            </Grid>
            <Grid>
              <NumberInput
                max={maxChildrenTravellers}
                min={0}
                onChange={onChildrenPassangersChange}
                value={passengers.children}
              />
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">Infants (Under 2)</Typography>
            </Grid>
            <Grid>
              <NumberInput
                max={maxInfantTravellers}
                min={0}
                onChange={onInfantPassangersChange}
                value={passengers.infants}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="column">
                <Divider sx={{ marginBottom: 2, marginTop: 2 }} variant="fullWidth" />
                <Button onClick={handleDone} variant="contained">
                  Done
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </Box>
  );
}

export default TravelersNumberSelector;
